import * as THREE from "three";
import { BRAND } from "../content/brand";

/**
 * K-Town arrival cinematic — full Hollywood-premiere treatment.
 *
 * Camera phases
 *  0        → T_STOP   Follow-cam: car decelerates toward venue
 *  T_STOP   → T_CRANE  Crane up: camera rises, reveals full building height + neon sign
 *  T_CRANE  → T_ARC    Side arc: camera orbits to reveal venue width, crowd, spotlights
 *  T_ARC    → ∞        Hero hold: camera locked, confetti + paparazzi + tap-gate
 *                      Cinematic waits for a tap before handing off to the outro
 *                      video — this way the outro's `video.play()` runs inside
 *                      a user gesture so it can have sound on iOS Safari.
 */

const T_STOP        = 2.0;
const T_CRANE       = 2.8;
const T_ARC         = 4.2;
const T_PROMPT_IN   = 0.6;  // delay after T_ARC before the tap prompt shows
const CAR_TRAVEL    = 16;

function lerp(a: number, b: number, t: number) { return a + (b - a) * t; }
function easeOut3(t: number) { return 1 - Math.pow(1 - t, 3); }
function easeInOut2(t: number) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}
function clamp01(t: number) { return Math.max(0, Math.min(1, t)); }

interface Confetto { mesh: THREE.Mesh; vy: number; rx: number; ry: number; vx: number; }
interface Pap      { light: THREE.PointLight; timer: number; }
interface NeonMat  { mat: THREE.MeshStandardMaterial; base: number; amp: number; freq: number; phase: number; }

export class ArrivalScene {
  readonly group: THREE.Group;

  private spots: THREE.SpotLight[] = [];
  private neons: NeonMat[] = [];
  private paps: Pap[] = [];
  private confetti: Confetto[] = [];
  private entranceL!: THREE.PointLight;
  private entranceR!: THREE.PointLight;

  private fadeEl!: HTMLDivElement;
  private titleEl!: HTMLDivElement;
  private tapGateEl!: HTMLDivElement;
  private promptShown = false;

  private t = 0;
  private active = false;
  private camera!: THREE.PerspectiveCamera;
  private playerGroup!: THREE.Group;
  private carStartZ = 0;
  private carEndZ   = 0;
  private savedFov = 70;
  private onDoneFn: () => void = () => {};

  constructor(parent: HTMLElement) {
    this.group = new THREE.Group();
    this.group.visible = false;
    this.buildDOM(parent);
    this.buildBuilding();
    this.buildGroundAndTrees();
    this.buildCrowd();
    this.buildLights();
    this.buildConfetti();
  }

  // ─────────────────────────── DOM overlays ────────────────────────────

  private buildDOM(parent: HTMLElement): void {
    this.fadeEl = document.createElement("div");
    this.fadeEl.style.cssText =
      "position:fixed;inset:0;background:#000;opacity:0;pointer-events:none;z-index:90;";
    parent.appendChild(this.fadeEl);

    // Cinematic title card — appears like a movie subtitle
    this.titleEl = document.createElement("div");
    this.titleEl.style.cssText =
      "position:fixed;bottom:18%;left:0;right:0;display:flex;flex-direction:column;" +
      "align-items:center;gap:6px;pointer-events:none;z-index:91;opacity:0;";
    this.titleEl.innerHTML = `
      <div style="
        font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
        font-size:clamp(11px,2.8vw,28px);font-weight:900;
        letter-spacing:0.42em;text-transform:uppercase;color:#fff;
        padding:10px 40px;
        border-top:1px solid rgba(255,42,85,0.8);
        border-bottom:1px solid rgba(255,42,85,0.8);
        text-shadow:0 0 18px rgba(255,42,85,1),0 0 36px rgba(255,42,85,0.5);
      ">NOW ARRIVING</div>
      <div style="
        font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
        font-size:clamp(9px,1.8vw,16px);font-weight:600;
        letter-spacing:0.28em;text-transform:uppercase;
        color:rgba(255,255,255,0.55);
      ">★&nbsp;&nbsp;${BRAND.artist.toUpperCase()}&nbsp;&nbsp;★</div>
    `;
    parent.appendChild(this.titleEl);

    // Tap-to-continue gate shown at the end of the cinematic. Keeps the
    // hero shot on screen and ensures the outro's video.play() lands
    // inside a user gesture so it can have sound on iOS.
    this.tapGateEl = document.createElement("div");
    this.tapGateEl.style.cssText =
      "position:fixed;inset:0;display:flex;align-items:center;justify-content:center;" +
      "z-index:92;opacity:0;pointer-events:none;transition:opacity 0.4s;" +
      "padding:24px;";
    this.tapGateEl.innerHTML = `
      <button type="button" style="
        pointer-events:auto;cursor:pointer;
        font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
        font-size:clamp(18px,4.5vw,28px);font-weight:900;
        letter-spacing:0.18em;text-transform:uppercase;
        color:#1a0a00;
        padding:18px 36px;border:none;border-radius:999px;
        background:linear-gradient(135deg,#ffcd3c 0%,#ff9a3d 50%,#ff2a55 100%);
        box-shadow:
          0 0 0 3px rgba(0,0,0,0.35),
          0 0 32px 4px rgba(255,205,60,0.55),
          0 0 80px 8px rgba(255,42,85,0.4),
          inset 0 -3px 0 rgba(0,0,0,0.18),
          inset 0 1px 0 rgba(255,255,255,0.45);
        animation:stay-pulse 1.6s ease-in-out infinite;
        text-shadow:0 1px 0 rgba(255,255,255,0.45);
        -webkit-tap-highlight-color:transparent;
      ">Stay for the Show!</button>
    `;
    // The whole gate is clickable as a generous tap target — covers the
    // case where the player taps near the button but not exactly on it.
    this.tapGateEl.addEventListener("click", () => this.handleTap());
    parent.appendChild(this.tapGateEl);

    // Inject the keyframe once. Scoped name so it doesn't collide.
    if (!document.getElementById("stay-pulse-kf")) {
      const style = document.createElement("style");
      style.id = "stay-pulse-kf";
      style.textContent = `
        @keyframes stay-pulse {
          0%, 100% { transform: scale(1);    filter: brightness(1); }
          50%      { transform: scale(1.06); filter: brightness(1.12); }
        }
      `;
      document.head.appendChild(style);
    }
  }

  private handleTap(): void {
    if (!this.active || !this.promptShown) return;
    this.finishCinematic();
  }

  private finishCinematic(): void {
    this.active = false;
    this.promptShown = false;
    this.group.visible = false;
    this.tapGateEl.style.opacity = "0";
    this.tapGateEl.style.pointerEvents = "none";
    this.camera.fov = this.savedFov;
    this.camera.updateProjectionMatrix();
    this.onDoneFn();
  }

  // ─────────────────────────── 3-D building ────────────────────────────

  private buildBuilding(): void {
    // ── Wide base / podium ──────────────────────────────────────────────
    const podiumMat = new THREE.MeshStandardMaterial({
      color: 0x0d0920, roughness: 0.65, metalness: 0.25,
    });
    const podium = new THREE.Mesh(new THREE.BoxGeometry(38, 9, 7), podiumMat);
    podium.position.set(0, 4.5, 0);
    this.group.add(podium);

    // ── Tall tower (recessed slightly) ─────────────────────────────────
    const towerMat = new THREE.MeshStandardMaterial({
      color: 0x0b0717, roughness: 0.7, metalness: 0.2,
    });
    const tower = new THREE.Mesh(new THREE.BoxGeometry(22, 22, 5), towerMat);
    tower.position.set(0, 20, 1);
    this.group.add(tower);

    // ── Facade window grid on tower front ──────────────────────────────
    const facTex = makeVenueFacade();
    const facMat = new THREE.MeshBasicMaterial({ map: facTex, transparent: true });
    const fac = new THREE.Mesh(new THREE.PlaneGeometry(21, 15), facMat);
    fac.position.set(0, 18, 3.55);
    this.group.add(fac);

    // ── K-TOWN LIVE main sign ───────────────────────────────────────────
    const signTex = makeMainSign();
    const signMat = new THREE.MeshBasicMaterial({ map: signTex, transparent: true });
    const sign = new THREE.Mesh(new THREE.PlaneGeometry(22, 6), signMat);
    sign.position.set(0, 28, 3.6);
    this.group.add(sign);

    // ── Small secondary signs on podium wings ──────────────────────────
    for (const [x, text, col] of [
      [-14.5, "서울 클럽",   "#ff2a55"],
      [ 14.5, "LIVE NOW",   "#4dd0e1"],
    ] as [number, string, string][]) {
      const tex = makeSmallSign(text, col);
      const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true });
      const m   = new THREE.Mesh(new THREE.PlaneGeometry(7, 2.8), mat);
      m.position.set(x, 6.5, 3.65);
      this.group.add(m);
    }

    // ── Horizontal neon tube strips ────────────────────────────────────
    const stripDefs: [number, number, number, number][] = [
      //  y     color      base  freq
      [  9,  0xff2a55,  1.6, 2.1 ],
      [ 12,  0xb94fff,  1.4, 3.3 ],
      [ 15,  0x4dd0e1,  1.8, 1.8 ],
      [ 18,  0xff2a55,  1.5, 2.8 ],
      [ 21,  0xb94fff,  1.7, 3.6 ],
      [ 24,  0x4dd0e1,  1.6, 2.2 ],
      [ 27,  0xffcd3c,  2.0, 1.5 ],  // gold crown
      [ 31,  0xff2a55,  2.2, 4.0 ],  // above sign
    ];
    for (const [y, color, base, freq] of stripDefs) {
      const mat = new THREE.MeshStandardMaterial({
        color, emissive: color, emissiveIntensity: base,
        roughness: 0.15, metalness: 0,
      });
      const geo  = new THREE.BoxGeometry(22.5, 0.15, 0.15);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(0, y, 3.6);
      this.group.add(mesh);
      this.neons.push({ mat, base, amp: base * 0.4, freq, phase: Math.random() * Math.PI * 2 });
    }

    // ── Vertical gold neon on tower corners ────────────────────────────
    for (const x of [-11, 11]) {
      const mat = new THREE.MeshStandardMaterial({
        color: 0xffcd3c, emissive: 0xffcd3c, emissiveIntensity: 2.0, roughness: 0.15,
      });
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(0.15, 22, 0.15), mat);
      mesh.position.set(x, 20, 3.6);
      this.group.add(mesh);
      this.neons.push({ mat, base: 2.0, amp: 0.6, freq: 1.4, phase: Math.random() * Math.PI * 2 });
    }

    // ── Vertical neon on podium outer corners ──────────────────────────
    for (const [x, color] of [[-19, 0xff2a55], [19, 0xb94fff]] as [number, number][]) {
      const mat = new THREE.MeshStandardMaterial({
        color, emissive: color, emissiveIntensity: 1.8, roughness: 0.15,
      });
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(0.15, 9, 0.15), mat);
      mesh.position.set(x, 4.5, 3.65);
      this.group.add(mesh);
      this.neons.push({ mat, base: 1.8, amp: 0.5, freq: 2.6, phase: Math.random() * Math.PI * 2 });
    }

    // ── Entrance arch ──────────────────────────────────────────────────
    const archMat = new THREE.MeshStandardMaterial({
      color: 0x1c0f35, emissive: 0x8800ff, emissiveIntensity: 0.55,
      roughness: 0.3, metalness: 0.8,
    });
    for (const x of [-3.6, 3.6]) {
      const pillar = new THREE.Mesh(new THREE.BoxGeometry(0.6, 9, 0.6), archMat);
      pillar.position.set(x, 4.5, 3.7);
      this.group.add(pillar);
    }
    const archBeam = new THREE.Mesh(new THREE.BoxGeometry(8.0, 0.5, 0.6), archMat);
    archBeam.position.set(0, 9.25, 3.7);
    this.group.add(archBeam);

    // ── Entrance canopy overhang ────────────────────────────────────────
    const canMat = new THREE.MeshStandardMaterial({
      color: 0x150b28, roughness: 0.5, metalness: 0.7,
    });
    const canopy = new THREE.Mesh(new THREE.BoxGeometry(11, 0.3, 5.5), canMat);
    canopy.position.set(0, 9.4, 6.2);
    this.group.add(canopy);

    // Glowing white underside of canopy
    const canGlowMat = new THREE.MeshStandardMaterial({
      color: 0xffffff, emissive: 0xffffff, emissiveIntensity: 1.4, roughness: 0.2,
    });
    const canGlow = new THREE.Mesh(new THREE.PlaneGeometry(10.5, 5), canGlowMat);
    canGlow.rotation.x = Math.PI / 2;
    canGlow.position.set(0, 9.15, 6.2);
    this.group.add(canGlow);
    this.neons.push({ mat: canGlowMat, base: 1.4, amp: 0.5, freq: 3.5, phase: 0 });

    // ── Fake cone searchlight beams pointing up ─────────────────────────
    const beamMat = new THREE.MeshBasicMaterial({
      color: 0xffffff, transparent: true, opacity: 0.055,
      side: THREE.DoubleSide, depthWrite: false,
    });
    for (const [x, rz] of [[-11, -0.22], [11, 0.22]] as [number, number][]) {
      const cone = new THREE.Mesh(new THREE.ConeGeometry(1.8, 40, 12, 1, true), beamMat.clone());
      cone.position.set(x, 31, 2);
      cone.rotation.z = rz;
      this.group.add(cone);
    }
  }

  // ─────────────────────────── Ground + trees ─────────────────────────

  private buildGroundAndTrees(): void {
    // Wet reflective tarmac
    const tarmacMat = new THREE.MeshStandardMaterial({
      color: 0x040210, roughness: 0.03, metalness: 0.92,
    });
    const tarmac = new THREE.Mesh(new THREE.PlaneGeometry(70, 45), tarmacMat);
    tarmac.rotation.x = -Math.PI / 2;
    tarmac.position.set(0, -0.02, 10);
    this.group.add(tarmac);

    // Red carpet
    const carpetMat = new THREE.MeshStandardMaterial({ color: 0x7a0000, roughness: 0.95 });
    const carpet = new THREE.Mesh(new THREE.PlaneGeometry(6, 18), carpetMat);
    carpet.rotation.x = -Math.PI / 2;
    carpet.position.set(0, 0.01, 10);
    this.group.add(carpet);

    // Gold rope barrier posts + ropes
    const postMat = new THREE.MeshStandardMaterial({
      color: 0xd4af37, emissive: 0x6a3c00, emissiveIntensity: 0.35,
      roughness: 0.2, metalness: 0.95,
    });
    const postGeo = new THREE.CylinderGeometry(0.08, 0.08, 1.4, 8);
    const ropeGeo = new THREE.CylinderGeometry(0.035, 0.035, 3.6, 6);
    for (let i = 0; i < 5; i++) {
      const z = 3.5 + i * 3.6;
      for (const x of [-4.2, 4.2]) {
        const post = new THREE.Mesh(postGeo, postMat);
        post.position.set(x, 0.7, z);
        this.group.add(post);
        if (i > 0) {
          const rope = new THREE.Mesh(ropeGeo, postMat);
          rope.rotation.z = Math.PI / 2;
          rope.position.set(x, 0.9, z - 1.8);
          this.group.add(rope);
        }
      }
    }

    // LA palm trees
    const trunkMat = new THREE.MeshStandardMaterial({ color: 0x5c4033, roughness: 0.9 });
    const leafMat  = new THREE.MeshStandardMaterial({ color: 0x194419, roughness: 0.9 });
    for (const [x, z, h] of [
      [-17, 16, 8], [17, 16, 7.5], [-15, 9, 7], [15, 9, 8.5],
    ] as [number, number, number][]) {
      const lean = (Math.random() - 0.5) * 0.18;
      const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.32, h, 7), trunkMat);
      trunk.position.set(x, h / 2, z);
      trunk.rotation.z = lean;
      const crown = new THREE.Mesh(new THREE.SphereGeometry(1.7, 7, 5), leafMat);
      crown.scale.y = 0.32;
      crown.position.set(x + lean * h, h + 0.2, z);
      this.group.add(trunk, crown);
    }
  }

  // ─────────────────────────── Crowd ───────────────────────────────────

  private buildCrowd(): void {
    const palette = [
      0xff9966, 0xffcc88, 0xff6688, 0xaadd88,
      0x88ccff, 0xddaaff, 0xffaacc, 0x99ffee,
      0xffdd44, 0xff88aa,
    ];
    for (let i = 0; i < 44; i++) {
      const col  = palette[i % palette.length];
      const mat  = new THREE.MeshStandardMaterial({ color: col, roughness: 0.85 });
      const body = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.27, 1.35, 7), mat);
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.3, 7, 5), mat);
      const side = i % 2 === 0 ? 1 : -1;
      const cx   = side * (4.8 + Math.random() * 11);
      const cz   = 0.5 + Math.random() * 12;
      body.position.set(cx, 0.675, cz);
      head.position.set(cx, 1.72, cz);
      this.group.add(body, head);

      // ~40% have arms up
      if (Math.random() < 0.4) {
        const armMat = new THREE.MeshStandardMaterial({ color: col, roughness: 0.85 });
        for (const dx of [-0.38, 0.38]) {
          const arm = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.9, 0.13), armMat);
          arm.position.set(cx + dx, 2.15, cz);
          arm.rotation.z = dx > 0 ? 0.55 : -0.55;
          this.group.add(arm);
        }
      }
    }
  }

  // ─────────────────────────── Lights ──────────────────────────────────

  private buildLights(): void {
    // 4 main sweeping spotlights
    const spotDefs: [number, number, number][] = [
      [-9,  26, 0xff4400],
      [ 9,  26, 0x4400ff],
      [-14, 24, 0xff00aa],
      [ 14, 24, 0x00aaff],
    ];
    for (const [x, y, color] of spotDefs) {
      const s = new THREE.SpotLight(color, 0, 65, Math.PI / 9, 0.5, 1);
      s.position.set(x, y, 12);
      s.target.position.set(0, 0, 8);
      this.group.add(s, s.target);
      this.spots.push(s);
    }

    // Warm + cool point lights inside the entrance arch
    this.entranceL = new THREE.PointLight(0xff6600, 0, 14);
    this.entranceL.position.set(-2, 5.5, 4.5);
    this.group.add(this.entranceL);

    this.entranceR = new THREE.PointLight(0x9900ff, 0, 14);
    this.entranceR.position.set(2, 5.5, 4.5);
    this.group.add(this.entranceR);

    // Paparazzi flash pool — 8 lights, off by default
    const papPos: [number, number, number][] = [
      [-13, 1.5, 15], [-7, 1.5, 14], [-2, 1.5, 16],
      [  3, 1.5, 15], [ 8, 1.5, 14], [13, 1.5, 15],
      [-10, 1.5, 11], [ 9, 1.5, 12],
    ];
    for (const [x, y, z] of papPos) {
      const l = new THREE.PointLight(0xffffff, 0, 22);
      l.position.set(x, y, z);
      this.group.add(l);
      this.paps.push({ light: l, timer: 0 });
    }
  }

  // ─────────────────────────── Confetti ────────────────────────────────

  private buildConfetti(): void {
    const colors = [0xff2a55, 0xffcd3c, 0x4dd0e1, 0xb94fff, 0x00ff88, 0xff8c00, 0xffffff, 0xff99cc];
    for (let i = 0; i < 60; i++) {
      // Alternate between square chips and ribbon strips
      const isRibbon = i % 3 === 0;
      const mat  = new THREE.MeshBasicMaterial({ color: colors[i % colors.length] });
      const size = 0.18 + Math.random() * 0.18;
      const geo  = isRibbon
        ? new THREE.BoxGeometry(size * 0.35, size * 1.8, 0.03)
        : new THREE.BoxGeometry(size, size, 0.04);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(
        (Math.random() - 0.5) * 22,
        6 + Math.random() * 10,
        (Math.random() - 0.5) * 10 + 6,
      );
      mesh.rotation.set(
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2,
      );
      mesh.visible = false;
      this.group.add(mesh);
      this.confetti.push({
        mesh,
        vy: -(2.2 + Math.random() * 3.0),
        vx: (Math.random() - 0.5) * 1.2,
        rx: (Math.random() - 0.5) * 7,
        ry: (Math.random() - 0.5) * 7,
      });
    }
  }

  // ─────────────────────────── Public API ──────────────────────────────

  play(
    camera: THREE.PerspectiveCamera,
    playerGroup: THREE.Group,
    onDone: () => void,
  ): void {
    this.camera      = camera;
    this.playerGroup = playerGroup;
    this.onDoneFn    = onDone;
    this.t           = 0;
    this.active      = true;
    this.carStartZ   = playerGroup.position.z;
    this.carEndZ     = this.carStartZ - CAR_TRAVEL;
    this.savedFov    = camera.fov;

    this.group.position.set(0, 0, this.carStartZ - 20);
    this.group.visible = true;

    for (const s of this.spots)   s.intensity = 0;
    this.entranceL.intensity = 0;
    this.entranceR.intensity = 0;
    for (const p of this.paps)    { p.light.intensity = 0; p.timer = 0; }
    for (const c of this.confetti) c.mesh.visible = false;

    this.fadeEl.style.transition = "none";
    this.fadeEl.style.opacity = "0";
    this.titleEl.style.transition = "none";
    this.titleEl.style.opacity = "0";
    this.tapGateEl.style.transition = "opacity 0.4s";
    this.tapGateEl.style.opacity = "0";
    this.tapGateEl.style.pointerEvents = "none";
    this.promptShown = false;
  }

  reset(): void {
    this.active = false;
    this.promptShown = false;
    this.group.visible = false;
    this.fadeEl.style.transition = "none";
    this.fadeEl.style.opacity = "0";
    this.titleEl.style.transition = "none";
    this.titleEl.style.opacity = "0";
    this.tapGateEl.style.transition = "none";
    this.tapGateEl.style.opacity = "0";
    this.tapGateEl.style.pointerEvents = "none";
  }

  // ─────────────────────────── Update ──────────────────────────────────

  update(dt: number): void {
    if (!this.active) return;
    this.t += dt;
    const t  = this.t;
    const cE = this.carEndZ;

    // The venue is ~40m wide. With the default vertical FOV the horizontal
    // FOV in portrait phones is too narrow and clips the sign/building. Set
    // FOV from a *horizontal* target so the wide facade fits regardless of
    // aspect ratio — landscape gets a tighter, more cinematic vertical FOV
    // for free.
    this.fitCameraToVenue();

    // ── Pulse all neon materials ──────────────────────────────────────
    for (const n of this.neons) {
      n.mat.emissiveIntensity = n.base + n.amp * Math.sin(t * n.freq + n.phase);
    }

    // ── Phase 1: Car decelerates (0 → T_STOP) ────────────────────────
    if (t <= T_STOP) {
      const p  = t / T_STOP;
      const e  = easeOut3(p);
      this.playerGroup.position.z = this.carStartZ - e * CAR_TRAVEL;

      // Smooth blend from game camera (z=7) into follow-cam
      const blend  = clamp01(t / 0.35);
      const cz     = this.playerGroup.position.z;
      const followZ = cz + 8 - p * 3;
      const followY = 4.2 + p * 1.5;
      this.camera.position.set(
        0,
        lerp(4.2, followY, blend),
        lerp(7,  followZ, blend),
      );
      this.camera.lookAt(
        0,
        lerp(1.2, 1.8, blend),
        lerp(-8,  cz - 5, blend),
      );

      // Title card fades in from t=1.2
      if (t > 1.2) {
        const tp = clamp01((t - 1.2) / 0.4);
        this.titleEl.style.opacity = tp.toFixed(3);
      }
    }

    // ── Phase 2: Crane up (T_STOP → T_CRANE) ─────────────────────────
    if (t > T_STOP && t <= T_CRANE) {
      const p = (t - T_STOP) / (T_CRANE - T_STOP);
      const e = easeInOut2(p);

      // Camera rises and tilts upward to reveal the building height + sign
      // Start: (0, 5.7, cE+5) look (0, 1.8, cE-5)
      // End:   (0, 14,  cE+7) look (0, 26,  cE-10) — looking at the K-TOWN LIVE sign
      this.camera.position.set(
        0,
        lerp(5.7, 14,   e),
        lerp(cE + 5, cE + 7, e),
      );
      this.camera.lookAt(0, lerp(1.8, 26, e), lerp(cE - 5, cE - 10, e));

      // Entrance lights and spots begin ramping
      const ramp = e;
      this.entranceL.intensity = ramp * 4;
      this.entranceR.intensity = ramp * 3.5;
      for (let i = 0; i < this.spots.length; i++) {
        this.spots[i].intensity = ramp * 5.5;
        this.spots[i].color.setHSL((t * 0.1 + i * 0.25) % 1, 1.0, 0.6);
      }

      // Title card fades out during crane
      const tf = clamp01((t - T_STOP) / 0.3);
      this.titleEl.style.opacity = (1 - tf).toFixed(3);
    }

    // ── Phase 3: Side arc (T_CRANE → T_ARC) ──────────────────────────
    if (t > T_CRANE && t <= T_ARC) {
      const p = (t - T_CRANE) / (T_ARC - T_CRANE);
      const e = easeInOut2(p);

      // Arc from looking-up-at-sign to wide hero shot from the side
      // Start: (0,  14, cE+7) look (0, 26,  cE-10)
      // End:   (14, 10, cE+10) look (0, 6,  cE-6)
      this.camera.position.set(
        lerp(0,  14,   e),
        lerp(14, 10,   e),
        lerp(cE + 7, cE + 10, e),
      );
      this.camera.lookAt(
        0,
        lerp(26, 6, e),
        lerp(cE - 10, cE - 6, e),
      );

      // Spotlights keep cycling
      for (let i = 0; i < this.spots.length; i++) {
        this.spots[i].color.setHSL((t * 0.15 + i * 0.25) % 1, 1.0, 0.6);
      }

      // Confetti starts halfway through the arc
      if (p > 0.45) {
        for (const c of this.confetti) c.mesh.visible = true;
      }
    }

    // ── Phase 4 (∞): Hero hold — camera locked, confetti + paparazzi.
    // The cinematic doesn't auto-end; a tap on the "Tap for Next" gate
    // calls finishCinematic() and hands off to the outro.
    if (t > T_ARC) {
      this.camera.position.set(14, 10, cE + 10);
      this.camera.lookAt(0, 6, cE - 6);

      for (let i = 0; i < this.spots.length; i++) {
        this.spots[i].color.setHSL((t * 0.22 + i * 0.25) % 1, 1.0, 0.6);
      }

      for (const c of this.confetti) {
        c.mesh.position.y += c.vy * dt;
        c.mesh.position.x += c.vx * dt;
        c.mesh.rotation.x += c.rx * dt;
        c.mesh.rotation.y += c.ry * dt;
        if (c.mesh.position.y < -1) {
          c.mesh.position.y = 12 + Math.random() * 6;
          c.mesh.position.x = (Math.random() - 0.5) * 22;
        }
      }

      for (const pap of this.paps) {
        if (pap.timer > 0) {
          pap.timer -= dt;
          if (pap.timer <= 0) pap.light.intensity = 0;
        } else if (Math.random() < dt * 2.5) {
          pap.light.intensity = 20 + Math.random() * 15;
          pap.timer = 0.04 + Math.random() * 0.07; // 40-110ms burst
        }
      }

      if (!this.promptShown && t > T_ARC + T_PROMPT_IN) {
        this.promptShown = true;
        this.tapGateEl.style.opacity = "1";
        this.tapGateEl.style.pointerEvents = "auto";
      }
    }
  }

  /**
   * Adjust camera FOV based on the current viewport aspect so the full
   * venue facade stays visible. Targets a horizontal field-of-view, which
   * is what matters for fitting wide content; the vertical FOV is derived.
   * On very tall (phone-portrait) viewports we cap the vertical FOV so we
   * don't end up with a fisheye distortion.
   */
  private fitCameraToVenue(): void {
    const aspect = this.camera.aspect;
    if (!isFinite(aspect) || aspect <= 0) return;
    const targetHFovDeg = 85;
    const targetHFovRad = (targetHFovDeg * Math.PI) / 180;
    const vFovRad = 2 * Math.atan(Math.tan(targetHFovRad / 2) / aspect);
    let vFovDeg = (vFovRad * 180) / Math.PI;
    // Clamp: keep within a believable range. 50° is a fine "cinematic" low
    // bound for wide landscape; 105° avoids the worst fisheye in portrait.
    if (vFovDeg < 50) vFovDeg = 50;
    if (vFovDeg > 105) vFovDeg = 105;
    if (Math.abs(this.camera.fov - vFovDeg) > 0.01) {
      this.camera.fov = vFovDeg;
      this.camera.updateProjectionMatrix();
    }
  }

  destroy(): void {
    this.fadeEl.remove();
    this.titleEl.remove();
    this.tapGateEl.remove();
  }
}

// ─────────────────────────── Canvas textures ─────────────────────────────

function makeVenueFacade(): THREE.CanvasTexture {
  const W = 512, H = 384;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d")!;

  ctx.fillStyle = "#0a0616";
  ctx.fillRect(0, 0, W, H);

  // Subtle horizontal band texture (concrete floors)
  const rows = 9, cols = 11;
  const winW = (W / cols) * 0.62;
  const winH = (H / rows) * 0.54;
  const warm = ["#fff2c0", "#ffc77a", "#ffaa55", "#ffdd99"];
  const cool = ["#4dd0e1", "#c77dff", "#a0b4ff", "#88eeff"];

  for (let r = 0; r < rows; r++) {
    // Subtle floor-band lines
    ctx.fillStyle = "rgba(255,255,255,0.02)";
    ctx.fillRect(0, r * (H / rows) - 1, W, 2);

    for (let col = 0; col < cols; col++) {
      const cx  = (col + 0.5) * (W / cols);
      const cy  = (r   + 0.5) * (H / rows);
      const lit = Math.random() > 0.2;
      const pal = Math.random() < 0.65 ? warm : cool;
      const clr = lit ? pal[Math.floor(Math.random() * pal.length)] : "#07041a";
      ctx.shadowBlur  = lit ? 5 : 0;
      ctx.shadowColor = clr;
      ctx.fillStyle   = clr;
      ctx.fillRect(cx - winW / 2, cy - winH / 2, winW, winH);
    }
  }
  ctx.shadowBlur = 0;

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function makeMainSign(): THREE.CanvasTexture {
  const W = 1024, H = 256;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d")!;
  ctx.clearRect(0, 0, W, H);

  // Rich dark backplate
  ctx.fillStyle = "rgba(5,2,16,0.95)";
  ctx.fillRect(0, 0, W, H);

  // Outer border glow
  ctx.strokeStyle = "#ff2a55";
  ctx.lineWidth   = 5;
  ctx.shadowBlur  = 30;
  ctx.shadowColor = "#ff2a55";
  ctx.strokeRect(6, 6, W - 12, H - 12);

  // Inner border (thinner, cyan)
  ctx.strokeStyle = "rgba(77,208,225,0.4)";
  ctx.lineWidth   = 1.5;
  ctx.shadowBlur  = 0;
  ctx.strokeRect(12, 12, W - 24, H - 24);

  // Main heading
  ctx.font          = "bold 130px sans-serif";
  ctx.textAlign     = "center";
  ctx.textBaseline  = "middle";
  // Multiple shadow passes for richness
  ctx.shadowBlur    = 60;
  ctx.shadowColor   = "#ff2a55";
  ctx.fillStyle     = "#ffffff";
  ctx.fillText("K-TOWN LIVE", W / 2, H * 0.4);
  ctx.shadowBlur    = 100;
  ctx.shadowColor   = "#b94fff";
  ctx.fillText("K-TOWN LIVE", W / 2, H * 0.4);
  ctx.shadowBlur    = 0;
  ctx.strokeStyle   = "#ff2a55";
  ctx.lineWidth     = 2.5;
  ctx.strokeText("K-TOWN LIVE", W / 2, H * 0.4);

  // Subtitle
  ctx.font          = "600 30px sans-serif";
  ctx.shadowBlur    = 16;
  ctx.shadowColor   = "#4dd0e1";
  ctx.fillStyle     = "#4dd0e1";
  ctx.fillText("★  NAZARETH SUEDEGOD  ★", W / 2, H * 0.8);
  ctx.shadowBlur    = 0;

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function makeSmallSign(text: string, color: string): THREE.CanvasTexture {
  const W = 512, H = 192;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d")!;
  ctx.clearRect(0, 0, W, H);

  ctx.fillStyle = "rgba(4,2,12,0.9)";
  ctx.fillRect(0, 0, W, H);

  ctx.strokeStyle = color;
  ctx.lineWidth   = 3;
  ctx.shadowBlur  = 18;
  ctx.shadowColor = color;
  ctx.strokeRect(6, 6, W - 12, H - 12);

  ctx.font          = "bold 88px sans-serif";
  ctx.textAlign     = "center";
  ctx.textBaseline  = "middle";
  ctx.shadowBlur    = 30;
  ctx.shadowColor   = color;
  ctx.fillStyle     = "#ffffff";
  ctx.fillText(text, W / 2, H / 2);
  ctx.shadowBlur    = 0;

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
